/* eslint-disable react/no-children-prop */

import React from 'react';
import ReactDOM from 'react-dom';
import Form from 'react-formal';
import { injectIntl } from 'react-intl';

import FormCheck from './FormCheck';
import FormControl from './FormControl';
import ValidationTooltip from './ValidationTooltip';

class FormField extends React.Component {
  getTarget = () => this.input.current;

  attachRef = current => {
    const { innerRef } = this.props;
    this.input = current && ReactDOM.findDOMNode(current); // eslint-disable-line
    if (typeof innerRef === 'function') innerRef(this.input);
    else if (innerRef) innerRef.current = this.input;
  };

  handleChange = e => {
    const { meta, onChange, checkedValue } = this.fieldProps;
    let value = e.target ? e.target.value : e;

    if (
      e.target &&
      e.target.type === 'checkbox' &&
      checkedValue === undefined
    ) {
      value = e.target.checked;
    }

    if (meta && meta.schema) {
      value = meta.schema.cast(value, { context: meta.context });
    }

    onChange(value);
  };

  maybeWithToolTip = (meta, child) => {
    const { withTooltip, placement, tooltipTheme } = this.props;
    if (!withTooltip) return child;

    return (
      <>
        {child}
        <ValidationTooltip
          target={this.input}
          show={meta.invalid}
          errors={meta.errors}
          placement={placement}
          theme={tooltipTheme}
        />
      </>
    );
  };

  render() {
    const {
      placeholder,
      intl,
      children,
      as: Input,
      withTooltip: _,
      placement: _1,
      tooltipTheme: _2,
      innerRef: _3,
      ...outerProps
    } = this.props;
    const formattedPlaceholder =
      !placeholder || typeof placeholder === 'string'
        ? placeholder
        : intl.formatMessage(placeholder, placeholder.values);

    return (
      <Form.Field {...outerProps}>
        {fieldProps => {
          if (typeof children === 'function') {
            return children(
              { ...fieldProps, placeholder: formattedPlaceholder },
              Input,
            );
          }

          this.fieldProps = fieldProps;

          const { checkedValue, meta, formControlAs, ...props } = fieldProps;
          const { resolvedType } = meta;

          props['aria-invalid'] = meta.invalid;

          if (Input && typeof Input !== 'string')
            return this.maybeWithToolTip(
              meta,
              <Input
                {...props}
                meta={meta}
                ref={this.attachRef}
                children={children}
                placeholder={formattedPlaceholder}
              />,
            );

          if (
            resolvedType === 'radio' ||
            resolvedType === 'boolean' ||
            resolvedType === 'checkbox'
          ) {
            const type = resolvedType === 'radio' ? 'radio' : 'checkbox';
            if (type === 'radio' || checkedValue !== undefined) {
              props.checked = checkedValue === props.value;
              props.value = checkedValue;
            } else {
              props.checked = !!props.value;
            }

            return this.maybeWithToolTip(
              meta,
              <FormCheck
                {...props}
                type={type}
                ref={this.attachRef}
                children={children}
                invalid={meta.invalid}
                onChange={this.handleChange}
              />,
            );
          }

          let component = 'input';
          if (resolvedType === 'textarea') component = 'textarea';
          if (resolvedType === 'select') component = 'select';
          return this.maybeWithToolTip(
            meta,
            <FormControl
              {...props}
              as={formControlAs || Input || component}
              children={children}
              ref={this.attachRef}
              invalid={meta.invalid}
              onChange={this.handleChange}
              placeholder={formattedPlaceholder}
            />,
          );
        }}
      </Form.Field>
    );
  }
}

export default injectIntl(FormField);
